import { reactive } from "vue";
import { Game } from "./index";
import "./handlers";
import { defaultGameData } from "../data";
import type { itemId } from "../data/types";
import { seededRandom } from "../lib/algo";

export const game = new Game(
  defaultGameData,
  reactive({
    tick: 0,
    items: new Map(),
    tiles: new Map(),
    size: { x: 15, y: 10 },
    dragging: null,
    highlight: {},
    scheduledEvents: [],
    visibilityThreshold: 1, // starts at orb-1 power
  }),
);

Object.assign(globalThis, { game });

game.grid.initialize(25, 13);

// Initialize shade on edges and place emitters
function initializeShadeMap() {
  const { x: width, y: height } = game.state.size;
  const centerX = Math.floor(width / 2);
  const centerY = Math.floor(height / 2);

  game.shade.applyDistanceShadeMap({
    center: { x: centerX, y: centerY },
    rimDistance: 4,
    rimShade: 100,
    rimStep: 50,
    bufferDistance: 3,
    bufferShade: 50,
  });

  // Place emitters in corners (covered by shade)
  const emitterPositions = [
    { x: 1, y: 1 },
    { x: width - 2, y: 1 },
    { x: 1, y: height - 2 },
    { x: width - 2, y: height - 2 },
  ];

  emitterPositions.forEach((pos, i) => {
    const emitterId = i < 2 ? "emitter-1" : "emitter-2";
    game.item.spawn({ itemId: emitterId as itemId, tile: game.grid.at(pos), animation: null });
  });

  // Place chests at edges
  const lootPositions = [
    { x: 0, y: centerY, item: "chest-1" },
    { x: width - 1, y: centerY, item: "chest-1" },
  ];

  lootPositions.forEach(({ x, y, item }) => {
    const tile = game.grid.tryAt({ x, y });
    if (tile && !tile.item) {
      game.item.spawn({ itemId: item as itemId, tile: tile, animation: null });
    }
  });

  // Scatter flower-1 items in shaded areas without creating adjacent merge clusters.
  const rng = seededRandom(12345); // Use consistent seed for reproducibility
  const shadedTiles = Array.from(game.state.tiles.values()).filter(
    (tile) => tile.shade > 0 && !tile.item,
  );

  // Keep the old target count, while rejecting groups large enough to merge.
  const flowerCount = Math.floor(shadedTiles.length * 0.5);
  const shuffled = shadedTiles.sort(() => rng() - 0.5);
  const occupied = new Set<string>();

  for (const tile of shuffled) {
    if (occupied.size >= flowerCount) break;
    if (!tile || tile.item) continue;
    const adjacentComponents = new Set<string>();
    let connectedSize = 1;
    for (const adjacent of game.grid.adjacent(tile.position)) {
      if (!occupied.has(adjacent.key) || adjacentComponents.has(adjacent.key)) continue;
      const component = occupiedComponent(adjacent.key);
      for (const key of component) adjacentComponents.add(key);
      connectedSize += component.length;
    }
    if (connectedSize > 2) {
      continue;
    }
    game.item.spawn({ itemId: "flower-1", tile: tile, animation: null });
    occupied.add(tile.key);
  }

  function occupiedComponent(startKey: string): string[] {
    const queue = [startKey];
    const seen = new Set<string>();
    for (let key = queue.shift(); key; key = queue.shift()) {
      if (seen.has(key)) continue;
      seen.add(key);
      const tile = game.state.tiles.get(key as any);
      if (!tile) continue;
      for (const adjacent of game.grid.adjacent(tile.position)) {
        if (occupied.has(adjacent.key) && !seen.has(adjacent.key)) {
          queue.push(adjacent.key);
        }
      }
    }
    return [...seen];
  }
}

// Initialize player starting area (center, no shade)
function initializePlayerArea() {
  const { x: width, y: height } = game.state.size;
  const centerX = Math.floor(width / 2);
  const centerY = Math.floor(height / 2);

  // Starting items for the player
  const startingItems: { dx: number; dy: number; item: itemId }[] = [
    { dx: -1, dy: -1, item: "orb-1" as itemId },
    { dx: 0, dy: -1, item: "orb-1" as itemId },
    { dx: -1, dy: 0, item: "flower-1" as itemId },
    { dx: 1, dy: 0, item: "flower-1" as itemId },
    { dx: 0, dy: 1, item: "orb-1" as itemId },
    { dx: 1, dy: 1, item: "orb-1" as itemId },
  ];

  startingItems.forEach(({ dx, dy, item }) => {
    const pos = { x: centerX + dx, y: centerY + dy };
    const tile = game.grid.at(pos);
    if (tile && !tile.item) {
      game.item.spawn({ itemId: item, tile: tile, animation: null });
    }
  });
}

// Run initialization
initializeShadeMap();
initializePlayerArea();

export const initialGameState = game.saveState();

export function resetGame() {
  game.loadState(JSON.parse(JSON.stringify(initialGameState)));
}

// Debug: also keep the row 0 spawning for testing (can be removed later)
const DEBUG_MODE = false;

if (DEBUG_MODE) {
  const spawnableItems = Object.values(game.data.items)
    .filter((item) => item.display)
    .map((item) => item.id);

  function spawnRow0() {
    spawnableItems.forEach((id, x) => {
      const tile = game.grid.tryAt({ x, y: 0 });
      if (tile && !tile.item) {
        game.item.spawn({ itemId: id, tile: tile, animation: null });
      }
    });
  }

  spawnRow0();

  game.on("ui:drop", ({ from }) => {
    if (from && from.position.y === 0) {
      const tile = game.grid.tryAt({ x: from.position.x, y: 0 });
      if (tile && !tile.item) {
        const id = spawnableItems[from.position.x];
        if (id) game.item.spawn({ itemId: id, tile: tile, animation: null });
      }
    }
  });
}
